import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  ArrowRight, 
  BookOpen, 
  Users, 
  TrendingUp, 
  Quote,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { 
  organizationInfo, 
  mission, 
  vision, 
  impactStats, 
  successStories 
} from '../data/mock';

const Home = () => {
  const quickLinks = [
    { 
      title: 'Programs', 
      description: 'Explore our Creative Learning Programs',
      icon: BookOpen,
      path: '/programs',
      color: 'bg-red-500'
    },
    { 
      title: 'Our Impact', 
      description: 'See the measurable difference we make in our community',
      icon: TrendingUp,
      path: '/impact',
      color: 'bg-gray-800'
    },
    { 
      title: 'Get Involved', 
      description: 'Volunteer, partner, or enroll your child in our programs',
      icon: Users,
      path: '/get-involved',
      color: 'bg-red-600'
    },
    { 
      title: 'Donate', 
      description: 'Support the next generation of scholars and leaders',
      icon: Heart,
      path: '/donate',
      color: 'bg-gray-900'
    },
  ];

  const featuredStory = successStories[0];

  return (
    <div>
      <SEO 
        title="Youth Mentoring & Tutoring in St. Louis"
        description="Mentors in Motion empowers St. Louis youth through tutoring, mentoring, and creative learning programs. 501(c)(3) nonprofit since 2009."
        keywords="youth mentoring St. Louis, tutoring nonprofit Missouri, after school programs St. Louis, creative learning, music production education, youth development nonprofit, mentors in motion, MiM STL"
        canonicalUrl="/"
      />
      
      {/* Hero Section - Lighter Background */}
      <section className="relative min-h-screen flex items-center bg-gray-100 overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.05),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(0,0,0,0.03),transparent_50%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              {/* Organization Name */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-4">
                Mentors In Motion
              </h1>
              
              {/* Tagline */}
              <p className="text-2xl sm:text-3xl font-semibold text-red-600 mb-6">
                {organizationInfo.tagline}
              </p>
              
              {/* Mission Statement */}
              <p className="text-lg text-gray-700 mb-10 leading-relaxed">
                {mission}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/donate">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 h-14 text-lg shadow-lg shadow-red-600/30 hover:shadow-xl hover:shadow-red-600/40 transition-all w-full sm:w-auto">
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button size="lg" variant="outline" className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Video */}
            <div className="flex flex-col items-center">
              {/* Embedded YouTube Video */}
              <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl bg-black">
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <iframe 
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/OowWKoTDVKM" 
                    title="Mentors In Motion 2025" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Strip */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl sm:text-6xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-lg text-gray-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="relative text-center">
              <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">Our Mission</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                {mission}
              </p>
            </div>
            <div className="relative text-center">
              <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">Our Vision</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                {vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get Started with Mentors in Motion
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you're looking to support, volunteer, or enroll, there's a place for you in our community.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <Link key={index} to={link.path}>
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 group overflow-hidden">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 ${link.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <link.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                      {link.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {link.description}
                    </p>
                    <div className="mt-4 flex items-center text-red-600 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn more
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
                Who We Serve
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Empowering Youth Across St. Louis
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                MiM serves <strong>Pre-K through 12th grade youth</strong> in the St. Louis Metro area, focusing on under-resourced communities and schools like Normandy Schools Collaborative.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full" />
                  </div>
                  <span className="text-gray-700">Founded in <strong>2009</strong> by local educator Alandon Pitts</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full" />
                  </div>
                  <span className="text-gray-700">Over <strong>15,000 scholars served</strong> in the St. Louis region</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full" />
                  </div>
                  <span className="text-gray-700">Mentoring, tutoring, and creative learning programs</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full" />
                  </div>
                  <span className="text-gray-700">Summer camps, after-school programs, and in-school electives</span>
                </li>
              </ul>

              <Link to="/about" className="inline-flex items-center gap-2 text-red-600 font-semibold mt-8 hover:gap-3 transition-all">
                Read Our Story
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <iframe 
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/VzG3bkAvo4c"
                    title="Mentors in Motion"
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Scholar Spotlight */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={featuredStory.image}
                alt={featuredStory.name}
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -right-6 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
                {featuredStory.achievement}
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-2 text-red-400 mb-4">
                <Quote className="w-8 h-8" />
                <span className="text-sm font-medium uppercase tracking-wider">Scholar Spotlight</span>
              </div>
              
              <blockquote className="text-base sm:text-lg font-medium leading-relaxed mb-6">
                "{featuredStory.quote}"
              </blockquote>
              
              <p className="text-gray-400 mb-6">
                {featuredStory.story}
              </p>
              
              <div className="flex items-center gap-4 mb-8">
                <div>
                  <div className="font-semibold text-lg">{featuredStory.name}</div>
                  <div className="text-gray-400">Class of {featuredStory.classOf}</div>
                </div>
              </div>
              
              <Link to="/thank-you">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  See Our Supporters
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Join Us in Building Thriving Communities
          </h2>
          <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto">
            Your support, whether through volunteering, partnership, or donation, directly transforms the lives of young scholars in St. Louis.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                <Heart className="w-5 h-5 mr-2" />
                Donate Today
              </Button>
            </Link>
            <Link to="/get-involved">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                Get Involved
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
